import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import injectSheet from 'react-jss';
import { Switch } from 'antd';
import { DateTime } from 'luxon';
import Datepicker from '@mtblc/react-mobile-datepicker';
import { useToggle } from '../../../hooks';
import { DEFAULT_LEAP_YEAR, DEFAULT_NO_YEAR, DEFAULT_YEAR } from '../../../lib/birthdays';
import { operatingSystems, getMobileOperatingSystem } from '../../../lib/download-links';
import Input from '../../Common/Forms/Input';
import { birthdayPropType, classesPropType } from '../../prop-types';

const FROM_YEAR = 1900;
const MIN_DATE = new Date(FROM_YEAR, 0, 1);
const MAX_DATE = new Date();
const WITH_YEAR_FORMAT = 'YYYY-MM-DD';
const NO_YEAR_FORMAT = 'MM-DD';

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  switch: {
    marginTop: '8px',
  },
  value: {
    flex: 1,
    marginRight: '5px',
  },
};

const themes = {
  [operatingSystems.ios]: 'ios',
  [operatingSystems.android]: 'android',
};

function BirthdayInput({ classes, onSelect, value }) {
  const { t, i18n: { language } } = useTranslation();

  const [pickerOpen, togglePicker, togglePickerOn, togglePickerOff] = useToggle(false);
  const [noYear, toggleNoYear] = useToggle(DEFAULT_NO_YEAR);

  const [prevYear, setPrevYear] = useState(() => (
    value.year || (noYear ? DEFAULT_LEAP_YEAR : DEFAULT_YEAR)
  ));

  const currentSelectedBirthdate = useMemo(() => DateTime.fromObject({
    ...value,
    year: value.year || prevYear,
  }), [value, prevYear]);

  const formattedDate = currentSelectedBirthdate
    .setLocale(language)
    .toFormat(t(`dates.display.${noYear ? 'withoutYear' : 'withYear'}`));

  const handleSelect = useCallback((date, withYear = !noYear, shouldTogglePicker = true) => {
    const dt = DateTime.fromJSDate(date);
    const selectedBirthday = { day: dt.day, month: dt.month };
    if (withYear) {
      selectedBirthday.year = dt.year;
      setPrevYear(dt.year);
    }
    onSelect(selectedBirthday);
    if (shouldTogglePicker) togglePicker();
  }, [noYear, onSelect, setPrevYear, togglePicker]);

  const handleChange = useCallback(
    date => handleSelect(date, !noYear, false),
    [noYear, handleSelect],
  );

  const handleToggleNoYear = useCallback(() => {
    const dateToSet = new Date(
      prevYear,
      currentSelectedBirthdate.month - 1, // Date object starts months at 0
      currentSelectedBirthdate.day,
    );

    if (!noYear) setPrevYear(value.year);

    handleSelect(dateToSet, noYear, false); // noYear has not yet changed
    toggleNoYear();
  }, [currentSelectedBirthdate, handleSelect, noYear, prevYear, toggleNoYear, value.year]);

  const datePickerValue = useMemo(
    () => new Date(currentSelectedBirthdate.ts),
    [currentSelectedBirthdate.ts],
  );

  const dateConfig = useMemo(
    () => {
      const baseConfig = {
        month: { format: 'MMMM', caption: t('dates.month') },
        date: { format: 'D', caption: t('dates.day') },
      };

      if (noYear) return baseConfig;

      return {
        ...baseConfig,
        year: { format: 'YYYY', caption: t('dates.year') },
      };
    },
    [t, noYear],
  );

  const onFieldFocus = useCallback(
    (e) => {
      togglePickerOn();
      e.target.blur();
    },
    [togglePickerOn],
  );

  return (
    <>
      <div className={classes.container}>
        <Input
          label={t('user.birthday')}
          value={formattedDate}
          onFocus={onFieldFocus}
          className={classes.value}
          readOnly
        />
        <Switch
          checked={noYear}
          checkedChildren={t('answerRequest.withoutYear')}
          unCheckedChildren={t('answerRequest.withYear')}
          onClick={handleToggleNoYear}
          className={classes.switch}
        />
      </div>

      <Datepicker
        cancelText={null}
        confirmText={t('answerRequest.confirmText')}
        dateConfig={dateConfig}
        headerFormat={noYear ? NO_YEAR_FORMAT : WITH_YEAR_FORMAT}
        min={MIN_DATE}
        max={MAX_DATE}
        isOpen={pickerOpen}
        onCancel={togglePickerOff}
        onSelect={handleSelect}
        onChange={handleChange}
        showCaption
        value={datePickerValue}
        theme={themes[getMobileOperatingSystem()]}
      />
    </>
  );
}

BirthdayInput.propTypes = {
  classes: classesPropType.isRequired,
  onSelect: PropTypes.func.isRequired,
  value: birthdayPropType.isRequired,
};

export default injectSheet(styles)(BirthdayInput);
